import useForm from '@/hooks/useForm/useForm';
import ButtonSubmit from '@/uikit/components/ButtonSubmit/ButtonSubmit';
import Form from '@/uikit/components/Form/Form';
import FormField from '@/uikit/components/FormField/FormField';
import { FormFieldType } from '@/uikit/components/FormField/consts';
import FormFieldRadioGroup from '@/uikit/components/FormFieldRadioGroup/FormFieldRadioGroup';
import FormRow from '@/uikit/components/FormRow/FormRow';
import Headline from '@/uikit/components/Headline/Headline';
import {
  HeadlineAlign,
  HeadlineColor,
} from '@/uikit/components/Headline/consts';
import Icon from '@/uikit/components/Icon/Icon';
import RichText from '@/uikit/components/RichText/RichText';
import Spacer from '@/uikit/components/Spacer/Spacer';
import Stack from '@/uikit/components/Stack/Stack';
import { FormEvent } from 'react';
import { ButtonSize, ButtonWidth } from '../Button/consts';
import CheckboxWithLabel from '../CheckboxWithLabel/CheckboxWithLabel';
import CourseMembersForm from '../CourseFormMembers/CourseMembersForm';
import { useCourseMembersForm } from '../CourseFormMembers/hooks';
import { durationDate } from '../CourseTimetable/helpers';
import FormOptInInfo from '../FormOptInInfo/FormOptInInfo';
import FormRowHeadline from '../FormRow/FormRowHeadline';
import FormToggleCard from '../FormToggleCard/FormToggleCard';
import styles from './CourseRegistrationForm.module.scss';
import { formSchema } from './schemas';
import type {
  CourseRegistrationFormProps,
  CourseRegistrationFormState,
} from './types';

const designationOptions = ['Herr', 'Frau', 'Divers'].map((value) => ({
  label: value,
  value,
}));

const initialState: CourseRegistrationFormState = {
  designation: '',
  firstName: '',
  lastName: '',
  company: '',
  postalCode: '',
  city: '',
  street: '',
  streetNumber: '',
  email: '',
  phone: '',
  annotations: '',
  hasDifferentBillingAddress: false,
  billingEmail: '',
  billingFirstName: '',
  billingLastName: '',
  billingCompany: '',
  billingPostalCode: '',
  billingCity: '',
  billingStreet: '',
  billingStreetNumber: '',
  marketingAgreement: false,
  privacyAgreement: false,
};

const CourseRegistrationForm = ({
  courseName,
  categoryName,
  coursePrice,
  endDate,
  startDate,
  onSubmit,
  submitButtonStatus,
  formName,
}: CourseRegistrationFormProps) => {
  const { isMembersFormValid, revalidateForm, ...courseMembersFormProps } =
    useCourseMembersForm();

  const {
    handleChange,
    handleBlur,
    formState,
    getErrorByFieldName,
    handleSubmit: handleUseFormSubmit,
    isFormValid,
  } = useForm(
    async (formState) => {
      if (!isMembersFormValid) {
        return;
      }

      onSubmit({ ...formState, members: courseMembersFormProps.members });
    },
    formSchema,
    initialState
  );

  const handleSubmit = async ($event: FormEvent<HTMLFormElement>) => {
    await revalidateForm();
    handleUseFormSubmit($event);
  };

  const getFieldProps = (name: string) => ({
    name,
    onBlur: handleBlur,
    onChange: handleChange,
    value: formState[name],
    errorMessage: getErrorByFieldName(name),
    errorMessageHasRelativePosition: true,
  });

  return (
    <Form className={styles.base} onSubmit={handleSubmit} name={formName}>
      <div>
        <Headline
          headlineColor={HeadlineColor.Gradient}
          align={HeadlineAlign.Center}
          headlineElementClassName={styles.formHeadline}
        >
          {`Anmeldung ${courseName}`}
        </Headline>

        <Spacer defaultSize={0.5} sm={1} />

        <RichText align="center">
          Hier können Sie sich und Ihre Mitarbeiter:innen zum {courseName}{' '}
          <strong>{`"${categoryName}"`}</strong> anmelden. Der Teilnahmepreis
          beträgt pro Teilnehmer:in <strong>{coursePrice}</strong>.
        </RichText>
      </div>

      <Stack
        flexDirection="row"
        gap="0.5rem"
        justifyContent="center"
        alignItems="center"
      >
        <Icon variant="user/calendar" size="iconSize24" isOutlined />

        {startDate && endDate && (
          <RichText size={'small'}>{durationDate(startDate, endDate)}</RichText>
        )}
      </Stack>

      <div>
        <FormRowHeadline>Teilnehmer:innen</FormRowHeadline>
        <CourseMembersForm {...courseMembersFormProps} />
      </div>

      <div>
        <FormRowHeadline>Ansprechpartner:in</FormRowHeadline>

        <FormRow bottomMarginSize={{ default: 1.25 }}>
          <FormFieldRadioGroup
            name={'designation'}
            items={designationOptions}
            value={formState.designation}
            onChange={handleChange}
            errorMessage={getErrorByFieldName('designation')}
            errorMessageHasRelativePosition
          />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField
            label="Vorname"
            {...getFieldProps('firstName')}
            autoComplete="given-name"
          />
          <FormField
            label="Nachname"
            {...getFieldProps('lastName')}
            autoComplete="family-name"
          />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField label="Firma" {...getFieldProps('company')} />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField
            label="Straße"
            {...getFieldProps('street')}
            autoComplete="address-line1"
          />
          <FormField
            label="Hausnummer"
            {...getFieldProps('streetNumber')}
            autoComplete="address-line2"
            halfWidth
          />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField
            label="PLZ"
            {...getFieldProps('postalCode')}
            maxLength={5}
            autoComplete="postal-code"
            halfWidth
          />
          <FormField
            label="Ort"
            {...getFieldProps('city')}
            autoComplete="address-level2"
          />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField
            label="E-Mail"
            {...getFieldProps('email')}
            type={FormFieldType.Email}
            autoComplete="email"
            halfWidth
          />
          <FormField
            label="Telefonnummer"
            {...getFieldProps('phone')}
            autoComplete="tel"
            halfWidth
          />
        </FormRow>

        <FormRow bottomMarginSize={{ default: 0 }}>
          <FormField
            label="Anmerkungen (optional)"
            {...getFieldProps('annotations')}
          />
        </FormRow>
      </div>

      <div>
        <FormRowHeadline>Rechnung</FormRowHeadline>

        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormField
            label="E-Mail (optional)"
            {...getFieldProps('billingEmail')}
          />
        </FormRow>

        <FormToggleCard
          label="Abweichende Rechnungsadresse"
          {...getFieldProps('hasDifferentBillingAddress')}
          checked={formState.hasDifferentBillingAddress}
        >
          <FormRow bottomMarginSize={{ default: 0.75 }}>
            <FormField label="Vorname" {...getFieldProps('billingFirstName')} />
            <FormField label="Nachname" {...getFieldProps('billingLastName')} />
          </FormRow>
          <FormRow bottomMarginSize={{ default: 0.75 }}>
            <FormField label="Firma" {...getFieldProps('billingCompany')} />
          </FormRow>
          <FormRow bottomMarginSize={{ default: 0.75 }}>
            <FormField label="Straße" {...getFieldProps('billingStreet')} />
            <FormField
              label="Hausnummer"
              {...getFieldProps('billingStreetNumber')}
            />
          </FormRow>
          <FormRow bottomMarginSize={{ default: 0.75 }}>
            <FormField
              label="Postleitzahl"
              {...getFieldProps('billingPostalCode')}
            />
            <FormField label="Ort" {...getFieldProps('billingCity')} />
          </FormRow>
        </FormToggleCard>
      </div>

      <CheckboxWithLabel
        label="Ich bin damit einverstanden, aktuelle Angebote und Informationen zu Schulungen, Netzdienstleistungen und Services der enercity AG per E-Mail zu erhalten. (optional)"
        id="marketingAgreement"
        name="marketingAgreement"
        checked={formState.marketingAgreement}
        onChange={handleChange}
        errorMessage={getErrorByFieldName('marketingAgreement')}
      />

      <Stack gap={'0.75rem'}>
        <CheckboxWithLabel
          label="Hiermit willige ich ein, dass die von mir angegebenen personenbezogenen Daten gemäß der Datenschutzerklärung verarbeitet werden."
          id="privacyAgreement"
          name="privacyAgreement"
          checked={formState.privacyAgreement}
          onChange={handleChange}
          errorMessage={getErrorByFieldName('privacyAgreement')}
        />

        <FormOptInInfo />
      </Stack>

      <div>
        <ButtonSubmit
          state={submitButtonStatus}
          size={ButtonSize.Medium}
          width={ButtonWidth.Auto}
          inactive={!isFormValid || !isMembersFormValid}
          withoutDataLayer
        >
          Anfrage einreichen
        </ButtonSubmit>
      </div>
    </Form>
  );
};

export default CourseRegistrationForm;
